// src/components/pages/Products.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  Divider,
  useTheme,
  Snackbar,
  Alert,
  Slide,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SeoHead from '../seo/SeoHead';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { bottles, detailsMap } from '../../data/Bottles';
import { useCart } from '../shopper/ShopperCart';

function SlideTransition(props) {
  return <Slide {...props} direction="down" />;
}

export default function Products() {
  const { id } = useParams();
  const nav = useNavigate();
  const theme = useTheme();
  const product = bottles.find((b) => b.id === +id);
  const { addItem } = useCart();

  // state
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // SEO props (guard against missing product)
 const title       = product
   ? `${product.name} – Marrakech Reserve Premium Olive Oil`
   : 'Product Not Found – Marrakech Reserve';
 const description = product
   ? product.description.slice(0, 160)
   : 'Browse our Marrakech Reserve catalogue of premium, small‑batch olive oils.';
 const url         = `https://marrakech-reserve.com/products/${id}`;
 const keywords    = product
   ? [
       product.name,
       product.region,
       product.grade,
       'extra virgin olive oil',
       'Marrakech Reserve',
     ].join(', ')
   : 'olive oil, Marrakech Reserve, premium';


  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  
  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(t);
  }, []);

  if (!product) {
    return (
      <Container sx={{ py: 6 }}>
  <Typography
    variant="h5"
    color="error"
    sx={{ fontFamily: '"Playfair Display", serif' }}
  >
    Product not found.
  </Typography>
  <Button
    onClick={() => nav('/products')}
    sx={{ mt: 2 }}
  >
    View More
  </Button>
</Container>

    );
  }

  const shortInfo = [
    ['Region', product.region],
    ['Grade', product.grade],
    ['Press', product.press],
  ];

  const handleAddToCart = () => {
    addItem(product, quantity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (

    
    <>
     {/* SEO meta tags for this product page */}
     <SeoHead
       title={title}
       description={description}
       url={url}
       keywords={keywords}
     />

    
      <Container
        component="section"
        sx={{
          py: { xs: 4, md: 8 },
          minHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 8 },
            alignItems: 'flex-start',
            flexGrow: 1,
          }}
        >
          {/* 1) Bottle Image */}
          <Box sx={{ flex: '2 1 60%', textAlign: 'center', overflow: 'hidden' }}>
            <motion.img
              src={product.img}
              alt={product.name}
              initial={{ y: '100vh', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 2, ease: 'easeOut' }}
              style={{ width: '130%', position: 'relative', left: '-15%' }}
            />
          </Box>

          {/* 2) Main Details */}
          <Box sx={{ flex: '1 1 40%', pr: { md: 4 } }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
    fontFamily: 'Montserrat, sans-serif',
                fontSize: { xs: '1.3rem', md: '1.5rem' },
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}
            >
              {product.name}
            </Typography>

            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{ fontFamily: '"Playfair Display", serif', mb: 2 }}
            >
              {product.volume} &nbsp;|&nbsp;{' '}
              <Box component="span" sx={{ fontWeight: 'bold' ,color: theme.palette.text.primary }}>
                {product.price}
              </Box>
            </Typography>

            <Divider sx={{ borderColor: theme.palette.text.primary, mb: 3, opacity: 0.3 }} />

            <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {detailsMap[product.id].map((d) => (
                <Chip
                  key={d.label}
                  label={d.label}
                  variant="outlined"
                  sx={{
                    borderColor: theme.palette.text.primary,
                    color: theme.palette.text.primary,
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>

            <Typography
              variant="body1"
              sx={{ mb: 4, fontFamily: 'Georgia, serif', lineHeight: 1.7 }}
            >
              {product.description}
            </Typography>

            <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {product.worksWith.map((w) => (
                <Chip
                  key={w}
                  label={w}
                  sx={{
                    bgcolor: theme.palette.grey[800],
                    color: theme.palette.common.white,
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 500,
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
              <IconButton size="small" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                <RemoveIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 32, textAlign: 'center' }}>
                {quantity}
              </Typography>
              <IconButton size="small" onClick={() => setQuantity((q) => q + 1)}>
                <AddIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={handleAddToCart}
                sx={{
                  backgroundColor: theme.palette.grey[900],
                  color: theme.palette.common.white,
                  fontFamily: '"Playfair Display", serif',
                  '&:hover': { backgroundColor: theme.palette.grey[800] },
                }}
              >
                Add to Cart
              </Button>
             <Button
  variant="outlined"
  size="large"
  fullWidth
  onClick={() => nav('/products')}
  sx={{
    borderColor: theme.palette.common.black,
    color: theme.palette.text.primary,
    fontFamily: '"Playfair Display", serif',
  }}
>
  View More
</Button>

            </Box>
          </Box>

          {/* 3) Sidebar */}
          <Box sx={{ flex: '1 1 20%', borderLeft: `1px solid ${theme.palette.divider}`, pl: { md: 4 } }}>
            <Typography
              variant="subtitle2"
              sx={{
                textTransform: 'uppercase',
                mb: 1,
                letterSpacing: '0.1em',
                fontWeight: 600,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Short Information
            </Typography>
            <List disablePadding sx={{ mb: 3 }}>
              {shortInfo.map(([k, v]) => (
                <ListItem key={k} sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Georgia, serif' }}>
                    <Box component="span" sx={{ color: theme.palette.text.primary, mr: 1 }}>•</Box>
                    <strong>{k}:</strong> {v}
                  </Typography>
                </ListItem>
              ))}
            </List>

            <Typography
              variant="subtitle2"
              sx={{
                textTransform: 'uppercase',
                mb: 1,
                letterSpacing: '0.1em',
                fontWeight: 600,
                fontFamily: '"Playfair Display", serif',
              }}
            >
              Tasting Notes
            </Typography>
            <List disablePadding>
              {product.tastingNotes.map((note) => (
                <ListItem key={note} sx={{ px: 0, py: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'Georgia, serif' }}>
                    {note}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Container>

      {/* Black-on-Black Snackbar */}
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        autoHideDuration={4000}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          icon={<CheckCircleIcon sx={{ color: theme.palette.common.white }} fontSize="inherit" />}
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{
            backgroundColor: theme.palette.common.black,
            color: theme.palette.common.white,
            borderRadius: 2,
            boxShadow: theme.shadows[6],
            fontSize: '1rem',
            '& .MuiAlert-icon': { fontSize: '1.2rem' },
          }}
        >
          Product added to cart!
        </Alert>
      </Snackbar>
    </>
  );
}
